import type { AppRouterInstance } from 'next/dist/shared/lib/app-router-context.shared-runtime';
import { deepLinkAnonymousSignInContext, parseDeepLinkResource } from '@/lib/auth/resourceDeepLink';

export type SignInSystemConfig = {
  basePath?: string;
  enableShibboleth?: boolean;
};

export type SignInContext = {
  title?: string;
  message?: string;
};

export const DEFAULT_SIGN_IN_TITLE = 'Sign In Required';

export const DEFAULT_SIGN_IN_MESSAGE =
  'You are not currently signed in. Please sign in to access this page and view your authorized content.';

export function buildSignInPageUrl(basePath: string, returnUrl: string, context?: SignInContext): string {
  const params = new URLSearchParams();
  params.set('returnUrl', returnUrl);
  if (context?.title) {
    params.set('title', context.title);
  }
  if (context?.message) {
    params.set('message', context.message);
  }
  const base = basePath.replace(/\/$/, '');
  return `${base}/sign-in?${params.toString()}`;
}

/**
 * Browser path to navigate to after sign-in.
 * returnUrl is typically already absolute (includes basePath); do not double-prefix.
 * Also accepts app-relative paths and prepends basePath once.
 */
export function resolvePostSignInPath(basePath: string, returnUrl?: string): string {
  const path = returnUrl && returnUrl.startsWith('/') ? returnUrl : '/';
  const base = (basePath || '').replace(/\/$/, '');
  if (!base) {
    return path;
  }
  if (path === base || path.startsWith(`${base}/`)) {
    return path;
  }
  return `${base}${path}`;
}

/**
 * Derive user-facing sign-in copy from the return URL (deep link the user tried to open).
 */
export function resolveSignInContextFromReturnUrl(returnUrl: string, basePath = ''): SignInContext {
  const relative = basePath && returnUrl.startsWith(basePath) ? returnUrl.slice(basePath.length) : returnUrl;
  const deepLink = parseDeepLinkResource(relative);
  if (deepLink) {
    return deepLinkAnonymousSignInContext(deepLink.kind, deepLink.label);
  }

  return {
    title: DEFAULT_SIGN_IN_TITLE,
    message: DEFAULT_SIGN_IN_MESSAGE,
  };
}

export function resolveSignInContext(returnUrl: string, basePath: string, explicit?: SignInContext): SignInContext {
  const fromUrl = resolveSignInContextFromReturnUrl(returnUrl, basePath);
  return {
    title: explicit?.title ?? fromUrl.title,
    message: explicit?.message ?? fromUrl.message,
  };
}

/** Microsoft is always available; Shibboleth/InCommon is an optional second method. */
export function hasMultipleSignInMethods(config?: SignInSystemConfig | null): boolean {
  return Boolean(config?.enableShibboleth);
}

/** App-relative return path (pathname + search + hash). */
export function getCurrentReturnUrl(): string {
  if (typeof window === 'undefined') {
    return '/';
  }
  return `${window.location.pathname}${window.location.search}${window.location.hash}`;
}

/**
 * Backend URL that starts the Shibboleth/InCommon sign-in redirect.
 * This is an API endpoint, not a Next.js route.
 */
export function buildShibbolethSignInUrl(apiEndpoint: string, returnUrl: string): string {
  const params = new URLSearchParams();
  params.set('returnUrl', returnUrl);
  const base = apiEndpoint.replace(/\/$/, '');
  return `${base}/auth/shibboleth?${params.toString()}`;
}

/**
 * Full-page navigate to the Shibboleth SP so the IdP can set cookies and return to the app.
 * Must not use the Next.js router: `/auth/shibboleth` is served by the API, not the UI.
 */
export function navigateToShibbolethSignIn(apiEndpoint: string | undefined | null, returnUrl: string): void {
  if (!apiEndpoint || typeof window === 'undefined') {
    return;
  }
  window.location.assign(buildShibbolethSignInUrl(apiEndpoint, returnUrl));
}

export type AnonymousSignInActions = {
  loginRedirect: (returnUrl?: string) => Promise<void>;
};

/**
 * Standard anonymous sign-in entry: MSAL redirect when Microsoft is the only method,
 * otherwise navigate to `/sign-in` so the user can choose.
 */
export async function initiateAnonymousSignIn(
  config: SignInSystemConfig | null | undefined,
  actions: AnonymousSignInActions,
  returnUrl: string,
  router?: AppRouterInstance,
  context?: SignInContext
): Promise<void> {
  if (!hasMultipleSignInMethods(config)) {
    await actions.loginRedirect(returnUrl);
    return;
  }

  const href = buildSignInPageUrl(config?.basePath ?? '', returnUrl, context);
  if (router) {
    router.replace(href);
  } else if (typeof window !== 'undefined') {
    window.location.assign(href);
  }
}
